import GuideLayout from '../../layouts/GuideLayout';
import type { TocItem } from '../../layouts/GuideLayout';
import { AffiliateCTA } from '../../components/content/AffiliateCTA';
import { CalculatorEmbed } from '../../components/content/CalculatorEmbed';

const tocItems: TocItem[] = [
  { id: 'four-components', label: 'The Four Cost Components', level: 2 },
  { id: 'fslogix-trap', label: 'FSLogix: Standard vs Premium', level: 2 },
  { id: 'session-hosts', label: 'Session Host Sizing & Auto-Shutdown', level: 2 },
  { id: 'user-licensing', label: 'User Licensing: Internal vs External', level: 2 },
  { id: 'avd-vs-w365', label: 'AVD vs Windows 365', level: 2 },
  { id: 'calculator', label: 'AVD Calculator', level: 2 },
];

const bottomCta = (
  <AffiliateCTA
    programme="Pluralsight"
    courseName="AZ-140 Configuring and Operating Microsoft Azure Virtual Desktop"
    url="https://www.pluralsight.com/paths/az-140-configuring-and-operating-microsoft-azure-virtual-desktop"
    price="From £29/month"
    badgeText="AVD Specialist"
  />
);

export default function AzureVirtualDesktopCostGuide() {
  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'Article',
            headline: 'Azure Virtual Desktop Pricing Guide UK 2026',
            description:
              'FSLogix Standard vs Premium trap, session host sizing, per-user licensing, AVD vs Windows 365 — in GBP for UK South.',
            author: { '@type': 'Person', name: 'AzureCalc.uk' },
            publisher: {
              '@type': 'Organization',
              name: 'AzureCalc.uk',
              url: 'https://www.azure-calc.co.uk',
            },
            dateModified: '2026-04-11',
            url: 'https://www.azure-calc.co.uk/guides/azure-virtual-desktop-pricing-uk',
          }),
        }}
      />
      <GuideLayout
        title="Azure Virtual Desktop Pricing Guide UK 2026"
        description="FSLogix on Standard tier routinely costs more than Premium once transactions are counted. Here's the full breakdown of what Azure Virtual Desktop actually costs in UK South."
        lastUpdated="April 2026"
        canonical="https://www.azure-calc.co.uk/guides/azure-virtual-desktop-pricing-uk"
        metaTitle="Azure Virtual Desktop Pricing Guide UK 2026 | AzureCalc.uk"
        metaDescription="Complete AVD cost breakdown for UK South — session hosts, FSLogix storage trap, per-user licensing, and AVD vs Windows 365 comparison."
        tocItems={tocItems}
        relatedCalculators={[
          { service: 'virtual-desktop', path: '/virtual-desktop', label: 'Virtual Desktop Calculator' },
          { service: 'virtual-machines', path: '/virtual-machines', label: 'Virtual Machines Calculator' },
        ]}
        bottomCta={bottomCta}
      >

        {/* ── Section 1: The Four Cost Components ──────────────────── */}
        <h2 id="four-components">The Four Cost Components</h2>

        <p>
          Azure Virtual Desktop has a well-documented hidden cost trap: FSLogix profile containers
          on Azure Files Standard tier. Most architects choose Standard tier because the per-GB
          price is lower — £0.056/GB/month vs Premium's £0.192/GB/month. But Standard tier charges
          per transaction, and an active VDI workload generates tens of thousands of transactions
          per user per day — login, logoff, app launches, file syncs. The transaction bill
          routinely exceeds the storage bill. Switch to Premium tier, pay more per GB, and your
          total storage cost drops. This is the single most common AVD cost optimisation.
        </p>

        <p>
          Before diving into that trap in detail, here are the four independent dimensions
          that make up an AVD deployment's cost:
        </p>

        <div className="not-prose grid grid-cols-1 sm:grid-cols-2 gap-3 my-4">
          <div className="rounded-lg p-4" style={{ backgroundColor: 'var(--surface-container)', border: '1px solid var(--outline-variant)' }}>
            <div className="flex items-center gap-2 mb-2">
              <span className="material-symbols-outlined text-[#0078d4] text-[18px]">computer</span>
              <p className="text-[13px] font-bold" style={{ color: 'var(--text-primary)' }}>Session Host VMs</p>
            </div>
            <p className="text-[12px]" style={{ color: 'var(--text-tertiary)' }}>
              VM size × count × hours. Typically 40–70% of total AVD cost for a 50-user deployment.
            </p>
          </div>
          <div className="rounded-lg p-4" style={{ backgroundColor: 'var(--surface-container)', border: '1px solid var(--outline-variant)' }}>
            <div className="flex items-center gap-2 mb-2">
              <span className="material-symbols-outlined text-[#0078d4] text-[18px]">folder_open</span>
              <p className="text-[13px] font-bold" style={{ color: 'var(--text-primary)' }}>FSLogix Profile Storage</p>
            </div>
            <p className="text-[12px]" style={{ color: 'var(--text-tertiary)' }}>
              Azure Files Standard or Premium. 10–30% of cost — but easily the most mis-estimated.
            </p>
          </div>
          <div className="rounded-lg p-4" style={{ backgroundColor: 'var(--surface-container)', border: '1px solid var(--outline-variant)' }}>
            <div className="flex items-center gap-2 mb-2">
              <span className="material-symbols-outlined text-[#0078d4] text-[18px]">group</span>
              <p className="text-[13px] font-bold" style={{ color: 'var(--text-primary)' }}>User Licensing</p>
            </div>
            <p className="text-[12px]" style={{ color: 'var(--text-tertiary)' }}>
              Per-user access fees. £0 for internal users with eligible M365. USD-denominated for external.
            </p>
          </div>
          <div className="rounded-lg p-4" style={{ backgroundColor: 'var(--surface-container)', border: '1px solid var(--outline-variant)' }}>
            <div className="flex items-center gap-2 mb-2">
              <span className="material-symbols-outlined text-[#0078d4] text-[18px]">swap_horiz</span>
              <p className="text-[13px] font-bold" style={{ color: 'var(--text-primary)' }}>Networking</p>
            </div>
            <p className="text-[12px]" style={{ color: 'var(--text-tertiary)' }}>
              RDP bandwidth egress. Usually &lt;5% of total cost but varies with video/graphics use.
            </p>
          </div>
        </div>

        {/* ── Section 2: FSLogix Trap ──────────────────────────────── */}
        <h2 id="fslogix-trap">FSLogix: Standard vs Premium — The Hidden Transaction Trap</h2>

        <p>
          Azure Files has two tiers relevant to FSLogix profile containers:
        </p>

        <div className="not-prose overflow-x-auto my-4">
          <table className="w-full text-[13px]" style={{ borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ borderBottom: '2px solid var(--outline-variant)' }}>
                <th className="text-left py-2 pr-4 font-bold" style={{ color: 'var(--text-tertiary)' }}></th>
                <th className="text-left py-2 pr-4 font-bold" style={{ color: 'var(--text-tertiary)' }}>Standard (HDD)</th>
                <th className="text-left py-2 font-bold" style={{ color: 'var(--text-tertiary)' }}>Premium (SSD)</th>
              </tr>
            </thead>
            <tbody>
              <tr style={{ borderBottom: '1px solid var(--outline-variant)' }}>
                <td className="py-2 pr-4 font-semibold" style={{ color: 'var(--text-secondary)' }}>Storage</td>
                <td className="py-2 pr-4" style={{ color: 'var(--text-secondary)' }}>£0.056/GB/month (pay for used)</td>
                <td className="py-2" style={{ color: 'var(--text-secondary)' }}>£0.192/GB/month (provisioned)</td>
              </tr>
              <tr style={{ borderBottom: '1px solid var(--outline-variant)' }}>
                <td className="py-2 pr-4 font-semibold" style={{ color: 'var(--text-secondary)' }}>Write ops</td>
                <td className="py-2 pr-4 text-[#e8b000]" >£0.0113/10K ops</td>
                <td className="py-2 text-[#107c10]">Included</td>
              </tr>
              <tr style={{ borderBottom: '1px solid var(--outline-variant)' }}>
                <td className="py-2 pr-4 font-semibold" style={{ color: 'var(--text-secondary)' }}>Read ops</td>
                <td className="py-2 pr-4" style={{ color: 'var(--text-secondary)' }}>£0.0011/10K ops</td>
                <td className="py-2 text-[#107c10]">Included</td>
              </tr>
              <tr>
                <td className="py-2 pr-4 font-semibold" style={{ color: 'var(--text-secondary)' }}>Performance</td>
                <td className="py-2 pr-4" style={{ color: 'var(--text-secondary)' }}>3–5 min login (IOPS limited)</td>
                <td className="py-2 text-[#107c10]">&lt;30s login (high IOPS)</td>
              </tr>
            </tbody>
          </table>
        </div>

        <p>
          The maths is straightforward. An active VDI user generates roughly 1,000–5,000 file system
          transactions per day: profile load at login, profile sync, application writes, logoff flush.
          For 50 users at 2,000 transactions/user/day (conservative):
        </p>

        <div className="not-prose bg-[#e8b000]/10 border border-[#e8b000]/50 rounded p-4 my-4">
          <p className="text-[12px] font-bold text-[#e8b000] mb-2">Example: 50 users, 500 GB provisioned, 2,000 tx/user/day</p>
          <div className="flex flex-col gap-1 text-[12px]" style={{ color: 'var(--text-secondary)' }}>
            <p><strong>Standard tier:</strong> Storage £28.20 + Transactions £31.59 = <strong className="text-[#e8b000]">£59.79/month</strong></p>
            <p><strong>Premium tier:</strong> Provisioned £96.10, no transactions = <strong className="text-[#107c10]">£96.10/month</strong></p>
          </div>
          <p className="text-[11px] mt-2" style={{ color: 'var(--text-tertiary)' }}>
            At 2,000 tx/user/day, Standard is still cheaper. But increase to 5,000 tx/user/day and Standard jumps to £98+.
            Real-world VDI workloads regularly exceed this, especially during concurrent login storms.
          </p>
        </div>

        <p>
          The break-even point depends on transaction volume. At around 3,500 transactions per user per
          day for 50 users on 500 GB, Standard tier matches Premium. Above that, Standard is
          <em>more expensive</em> despite having a lower per-GB rate. And you get 3–5 minute login times
          as a bonus downside.
        </p>

        <div className="not-prose bg-[#107c10]/10 border border-[#107c10]/40 p-4 flex gap-3 items-start rounded-sm my-4">
          <span className="material-symbols-outlined text-[#107c10]">lightbulb</span>
          <p className="text-sm" style={{ color: 'var(--text-primary)' }}>
            <strong className="text-[#107c10]">Recommendation:</strong> Use Premium tier for any production AVD deployment
            with more than 20 concurrent users. The login performance improvement alone justifies it — the
            cost savings from eliminating transactions is a bonus.
          </p>
        </div>

        {/* ── Section 3: Session Hosts ─────────────────────────────── */}
        <h2 id="session-hosts">Session Host Sizing and Auto-Shutdown</h2>

        <h3>Multi-session vs Personal Desktop</h3>

        <p>
          AVD supports two hosting models. <strong>Multi-session</strong> runs Windows 10/11
          Enterprise multi-session, hosting multiple users per VM. This is the cost-effective
          choice for task workers and general office use — a D4s v3 (4 vCPU, 16 GB RAM) can
          typically handle 8–12 concurrent light users.
        </p>

        <p>
          <strong>Personal desktop</strong> gives each user a dedicated VM. This suits developers,
          power users, or workloads requiring GPU. It costs 5–10× more per user but provides
          isolation and consistent performance.
        </p>

        <h3>Auto-shutdown saves ~65%</h3>

        <p>
          Session host VMs are the largest cost component. An always-on D4s v3 Windows VM costs
          approximately <strong>£229/month</strong> (£0.313/hr × 730 hrs). If your users only
          work weekday business hours (8am–6pm, Mon–Fri), that's roughly 260 hours/month —
          reducing VM cost to <strong>£81/month</strong>, a 65% saving.
        </p>

        <div className="not-prose bg-[#0078d4]/10 border border-[#0078d4]/30 p-4 flex gap-3 items-start rounded-sm my-4">
          <span className="material-symbols-outlined text-[#0078d4]">info</span>
          <p className="text-sm" style={{ color: 'var(--text-primary)' }}>
            Use Azure Automation or AVD scaling plans to automatically deallocate session hosts
            outside working hours. Scaling plans can ramp up hosts before login storms and drain
            sessions gracefully before shutdown.
          </p>
        </div>

        <h3>Azure Hybrid Benefit</h3>

        <p>
          If your organisation has Windows Server licences with active Software Assurance,
          Azure Hybrid Benefit lets you pay the Linux VM rate for Windows session hosts.
          On a D4s v3, this saves <strong>£0.138/hr per VM</strong> (Windows rate £0.313
          minus Linux rate £0.175). For 5 always-on VMs, that's ~£504/month saved.
        </p>

        {/* ── Section 4: User Licensing ────────────────────────────── */}
        <h2 id="user-licensing">User Licensing: Internal vs External</h2>

        <p>
          This is the pricing dimension that "licensing experts had no idea about" according
          to engineers who contacted Microsoft's licensing team. AVD has per-user access fees
          that are separate from the infrastructure costs:
        </p>

        <div className="not-prose overflow-x-auto my-4">
          <table className="w-full text-[13px]" style={{ borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ borderBottom: '2px solid var(--outline-variant)' }}>
                <th className="text-left py-2 pr-4 font-bold" style={{ color: 'var(--text-tertiary)' }}>User Type</th>
                <th className="text-left py-2 pr-4 font-bold" style={{ color: 'var(--text-tertiary)' }}>Fee</th>
                <th className="text-left py-2 font-bold" style={{ color: 'var(--text-tertiary)' }}>Notes</th>
              </tr>
            </thead>
            <tbody>
              <tr style={{ borderBottom: '1px solid var(--outline-variant)' }}>
                <td className="py-2 pr-4 font-semibold" style={{ color: 'var(--text-secondary)' }}>Internal (M365 E3/E5/F3/Business Premium)</td>
                <td className="py-2 pr-4 text-[#107c10] font-bold">£0/user</td>
                <td className="py-2" style={{ color: 'var(--text-tertiary)' }}>AVD access included in eligible M365 licence</td>
              </tr>
              <tr style={{ borderBottom: '1px solid var(--outline-variant)' }}>
                <td className="py-2 pr-4 font-semibold" style={{ color: 'var(--text-secondary)' }}>Internal (no eligible M365)</td>
                <td className="py-2 pr-4 text-[#e8b000] font-bold">~$8/user/month</td>
                <td className="py-2" style={{ color: 'var(--text-tertiary)' }}>USD — GBP equivalent varies (~£6.30)</td>
              </tr>
              <tr style={{ borderBottom: '1px solid var(--outline-variant)' }}>
                <td className="py-2 pr-4 font-semibold" style={{ color: 'var(--text-secondary)' }}>External — Apps only</td>
                <td className="py-2 pr-4 font-bold" style={{ color: 'var(--text-secondary)' }}>$5.50/user/month</td>
                <td className="py-2" style={{ color: 'var(--text-tertiary)' }}>RemoteApp only, no full desktop</td>
              </tr>
              <tr>
                <td className="py-2 pr-4 font-semibold" style={{ color: 'var(--text-secondary)' }}>External — Apps + Desktops</td>
                <td className="py-2 pr-4 font-bold" style={{ color: 'var(--text-secondary)' }}>$10/user/month</td>
                <td className="py-2" style={{ color: 'var(--text-tertiary)' }}>Full desktop experience for external users</td>
              </tr>
            </tbody>
          </table>
        </div>

        <p>
          The critical takeaway: if all your internal users already have M365 E3, E5, F3, or
          Business Premium, AVD access costs <strong>nothing extra</strong> from a licensing
          perspective. The entire cost is infrastructure (VMs + storage + networking).
        </p>

        {/* ── Section 5: AVD vs Windows 365 ────────────────────────── */}
        <h2 id="avd-vs-w365">AVD vs Windows 365</h2>

        <p>
          Windows 365 is Microsoft's fixed-price Cloud PC offering. It charges a flat monthly
          fee per user (£20–£162/user/month depending on spec) with no separate VM, storage,
          or licensing costs. The comparison:
        </p>

        <div className="not-prose grid grid-cols-1 sm:grid-cols-2 gap-3 my-4">
          <div className="rounded-lg p-4" style={{ backgroundColor: 'var(--surface-container)', border: '1px solid var(--outline-variant)' }}>
            <p className="text-[13px] font-bold text-[#0078d4] mb-2">Azure Virtual Desktop</p>
            <ul className="flex flex-col gap-1 text-[12px]" style={{ color: 'var(--text-secondary)' }}>
              <li>✅ Multi-session — share VMs across users</li>
              <li>✅ Auto-shutdown — pay only for active hours</li>
              <li>✅ Custom VM sizes — optimise for workload</li>
              <li>✅ Cheaper at scale with M365 licences</li>
              <li>⚠️ Requires infrastructure management</li>
              <li>⚠️ Cost varies with usage patterns</li>
            </ul>
          </div>
          <div className="rounded-lg p-4" style={{ backgroundColor: 'var(--surface-container)', border: '1px solid var(--outline-variant)' }}>
            <p className="text-[13px] font-bold text-[#0078d4] mb-2">Windows 365</p>
            <ul className="flex flex-col gap-1 text-[12px]" style={{ color: 'var(--text-secondary)' }}>
              <li>✅ Fixed predictable cost per user</li>
              <li>✅ Zero infrastructure management</li>
              <li>✅ Instant provisioning</li>
              <li>✅ Simpler for small teams (&lt;20 users)</li>
              <li>⚠️ No multi-session — 1 VM per user</li>
              <li>⚠️ No auto-shutdown — always billed</li>
            </ul>
          </div>
        </div>

        <p>
          <strong>When AVD wins:</strong> Large deployments (50+ users) with eligible M365 licences,
          variable working hours, and IT teams comfortable managing Azure infrastructure. AVD's
          multi-session and auto-shutdown capabilities can reduce per-user costs to £15–£30/month
          for task workers.
        </p>

        <p>
          <strong>When Windows 365 wins:</strong> Small teams (&lt;20 users), organisations without
          dedicated Azure admins, or when predictable per-user billing is a hard requirement.
        </p>

        {/* ── Section 6: Calculator ────────────────────────────────── */}
        <h2 id="calculator">Estimate Your AVD Costs</h2>

        <CalculatorEmbed service="virtual-desktop" />

        <p className="text-[12px] italic mt-4" style={{ color: 'var(--text-tertiary)' }}>
          Built and verified by an independent Azure engineer. Prices sourced from the Azure Retail
          Prices API and Azure pricing pages, UK South region, GBP. AVD access fees remain
          USD-denominated and are shown as approximate GBP equivalents.
        </p>

      </GuideLayout>
    </>
  );
}
